import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  CircularProgress,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Link
} from '@mui/material';
import {
  Sync,
  Edit,
  Save,
  Cancel,
  CheckCircle,
  Error,
  Language,
  TableChart,
  Refresh,
  Launch,
  DateRange
} from '@mui/icons-material';
const ListSiteForms = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSites, setSelectedSites] = useState([]);
  const [editingSites, setEditingSites] = useState({});
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [syncProgress, setSyncProgress] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [dateRange, setDateRange] = useState({
    fromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchPluginInstalledSites();
  }, []);

  const fetchPluginInstalledSites = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/sites/plugin-installed`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSites(data);
      } else {
        const errorText = await response.text();
        setError(`Failed to fetch plugin-installed sites: ${response.status} ${errorText}`);
      }
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedSites(sites.map(site => site.id));
    } else {
      setSelectedSites([]);
    }
  };

  const handleSelectSite = (siteId) => {
    setSelectedSites(prev => 
      prev.includes(siteId) 
        ? prev.filter(id => id !== siteId)
        : [...prev, siteId]
    );
  };

  const handleEditStart = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    setEditingSites(prev => ({
      ...prev,
      [siteId]: site.google_sheet_id || ''
    }));
  };

  const handleEditCancel = (siteId) => {
    setEditingSites(prev => {
      const newState = { ...prev };
      delete newState[siteId];
      return newState;
    });
  };

  const handleEditSave = async (siteId) => {
    const newGoogleSheetId = editingSites[siteId];
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/sites/${siteId}/google-sheet-id`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ google_sheet_id: newGoogleSheetId })
      });
      
      if (response.ok) {
        // Update local state
        setSites(prev => prev.map(site => 
          site.id === siteId 
            ? { ...site, google_sheet_id: newGoogleSheetId }
            : site
        ));
        handleEditCancel(siteId);
      } else {
        setError('Failed to update Google Sheet ID');
      }
    } catch (err) {
      setError(`Error updating Google Sheet ID: ${err.message}`);
    }
  };

  const handleEditChange = (siteId, value) => {
    setEditingSites(prev => ({
      ...prev,
      [siteId]: value
    }));
  };

  const handleSyncSelected = () => {
    if (selectedSites.length === 0) {
      setError('Please select at least one site to sync');
      return;
    }
    
    setSyncModalOpen(true);
    setSyncProgress(selectedSites.map(siteId => {
      const site = sites.find(s => s.id === siteId);
      return {
        siteId,
        siteName: site.rootdomain,
        status: 'pending',
        message: 'Waiting to sync...'
      };
    }));
  };

  const startSync = async () => {
    setSyncing(true);
    
    for (let i = 0; i < selectedSites.length; i++) {
      const siteId = selectedSites[i];

      // Update progress to show current site being processed
      setSyncProgress(prev => prev.map(item => 
        item.siteId === siteId 
          ? { ...item, status: 'syncing', message: 'Syncing Google Sheet...' }
          : item
      ));
      
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/google-sheets/${siteId}/create-google-sheet`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            from_date: `${dateRange.fromDate} 00:00:00`,
            to_date: `${dateRange.toDate} 23:59:59`,
            share_emails: []
          })
        });
        
        if (response.ok) {
          setSyncProgress(prev => prev.map(item => 
            item.siteId === siteId 
              ? { ...item, status: 'success', message: 'Successfully synced Google Sheet' }
              : item
          ));
        } else {
          const errorData = await response.json();
          setSyncProgress(prev => prev.map(item => 
            item.siteId === siteId 
              ? { 
                  ...item, 
                  status: 'error', 
                  message: `Failed to sync: ${errorData.detail || 'Unknown error'}` 
                }
              : item
          ));
        }
      } catch (err) {
        setSyncProgress(prev => prev.map(item => 
          item.siteId === siteId 
            ? { 
                ...item, 
                status: 'error', 
                message: `Network error: ${err.message}` 
              }
            : item
        ));
      }
      
      // Small delay between requests to prevent overwhelming the server
      if (i < selectedSites.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    setSyncing(false);
    // Refresh the sites list after sync
    await fetchPluginInstalledSites();
  };

  const closeSyncModal = () => {
    setSyncModalOpen(false);
    setSyncProgress([]);
    setSelectedSites([]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatEmails = (emailsString) => {
    if (!emailsString) return 'None';
    try {
      const emails = JSON.parse(emailsString);
      return Array.isArray(emails) ? emails.join(', ') : emailsString;
    } catch {
      return emailsString || 'None';
    }
  };

  const getGoogleSheetsUrl = (sheetId) => {
    if (!sheetId) return null;
    return `https://docs.google.com/spreadsheets/d/${sheetId}`;
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const setDateRangePreset = (preset) => {
    const today = new Date();
    let fromDate, toDate;

    switch (preset) {
      case 'today':
        fromDate = toDate = today.toISOString().split('T')[0];
        break;
      case 'thisWeek':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        fromDate = startOfWeek.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'thisMonth':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'last30Days':
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        fromDate = thirtyDaysAgo.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      default:
        return;
    }

    setDateRange({ fromDate, toDate });
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ pt: 4, pb: 6 }}>
        {/* Header */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 4, 
            mb: 4, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TableChart sx={{ fontSize: 48, mr: 3, opacity: 0.9 }} />
              <Box>
                <Typography variant="h3" component="h1" fontWeight="600" sx={{ mb: 1 }}>
                  List Site Forms
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                  Manage Google Sheets integration for plugin-installed sites
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={fetchPluginInstalledSites}
              disabled={loading}
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' }
              }}
            >
              Refresh
            </Button>
          </Box>
        </Paper>

        {/* Actions Bar */}
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={3} alignItems="center">
            {/* Date Range Section */}
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <DateRange sx={{ color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  Date Range for Sync
                </Typography>
              </Box>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="From Date"
                    value={dateRange.fromDate}
                    onChange={(e) => handleDateChange('fromDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="To Date"
                    value={dateRange.toDate}
                    onChange={(e) => handleDateChange('toDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Selected range: {Math.ceil((new Date(dateRange.toDate) - new Date(dateRange.fromDate)) / (1000 * 60 * 60 * 24)) + 1} days
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={() => setDateRangePreset('today')}
                        sx={{ fontSize: '0.75rem', py: 0.5 }}
                      >
                        Today
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={() => setDateRangePreset('thisWeek')}
                        sx={{ fontSize: '0.75rem', py: 0.5 }}
                      >
                        This Week
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={() => setDateRangePreset('thisMonth')}
                        sx={{ fontSize: '0.75rem', py: 0.5 }}
                      >
                        This Month
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={() => setDateRangePreset('last30Days')}
                        sx={{ fontSize: '0.75rem', py: 0.5 }}
                      >
                        Last 30 Days
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Actions Section */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedSites.length} site{selectedSites.length !== 1 ? 's' : ''} selected
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Sync />}
                  onClick={handleSyncSelected}
                  disabled={selectedSites.length === 0 || syncing}
                  size="large"
                  sx={{ 
                    minWidth: 180,
                    borderRadius: 2,
                    fontWeight: 600
                  }}
                >
                  Sync Selected Sites
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 4, borderRadius: 2 }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}

        {/* Information Alert */}
        <Alert
          severity="info"
          sx={{ mb: 4, borderRadius: 2 }}
        >
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Important Notes:
          </Typography>
          <Typography variant="body2" component="div">
            • If you want to see updated form entries in Google Sheets, please click the "Sync Selected Sites" button before opening the Google Sheets link.
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 1 }}>
            • <strong>IMPORTANT:</strong> We have a scheduled sync of form entries to Google Sheets every 2 hours. Please export or save your selected data before it can be overwritten by the automated sync.
          </Typography>
        </Alert>

        {/* Sites Table */}
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'primary.main' }}>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedSites.length > 0 && selectedSites.length < sites.length}
                        checked={sites.length > 0 && selectedSites.length === sites.length}
                        onChange={handleSelectAll}
                        sx={{ color: 'white' }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Root Domain</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Google Sheet ID</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Last Updated</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Shared Emails</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sites.map((site) => (
                    <TableRow 
                      key={site.id}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        bgcolor: selectedSites.includes(site.id) ? 'primary.light' : 'transparent'
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSites.includes(site.id)}
                          onChange={() => handleSelectSite(site.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Language sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2" fontWeight={500}>
                            {site.rootdomain}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {editingSites[site.id] !== undefined ? (
                          <TextField
                            size="small"
                            value={editingSites[site.id]}
                            onChange={(e) => handleEditChange(site.id, e.target.value)}
                            placeholder="Enter Google Sheet ID"
                            sx={{ minWidth: 200 }}
                          />
                        ) : (
                          <Box>
                            {site.google_sheet_id ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Link
                                  href={getGoogleSheetsUrl(site.google_sheet_id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ 
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                  }}
                                >
                                  <Chip 
                                    label={site.google_sheet_id} 
                                    size="small" 
                                    color="success"
                                    variant="outlined"
                                    clickable
                                    icon={<Launch sx={{ fontSize: 16 }} />}
                                    sx={{ 
                                      '&:hover': { 
                                        bgcolor: 'success.light',
                                        color: 'success.dark'
                                      }
                                    }}
                                  />
                                </Link>
                              </Box>
                            ) : (
                              <Chip 
                                label="Not set" 
                                size="small" 
                                color="default"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(site.google_sheet_last_updated)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                          {formatEmails(site.google_sheet_shared_emails)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {editingSites[site.id] !== undefined ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Save">
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditSave(site.id)}
                                color="primary"
                              >
                                <Save />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditCancel(site.id)}
                                color="default"
                              >
                                <Cancel />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Tooltip title="Edit Google Sheet ID">
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditStart(site.id)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {!loading && sites.length === 0 && (
            <Box sx={{ textAlign: 'center', p: 6 }}>
              <Language sx={{ fontSize: 80, color: 'primary.light', mb: 2, opacity: 0.7 }} />
              <Typography variant="h6" color="text.primary" gutterBottom>
                No Plugin-Installed Sites Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sites with the plugin installed will appear here.
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Sync Progress Modal */}
        <Dialog 
          open={syncModalOpen} 
          onClose={syncing ? undefined : closeSyncModal}
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Sync sx={{ mr: 2 }} />
              Sync Progress
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'primary.light', 
                borderRadius: 2, 
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}>
                <DateRange sx={{ color: 'primary.main' }} />
                <Box>
                  <Typography variant="body2" fontWeight={600} color="primary.dark">
                    Syncing Date Range
                  </Typography>
                  <Typography variant="body2" color="primary.dark">
                    {dateRange.fromDate} to {dateRange.toDate}
                  </Typography>
                </Box>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(syncProgress.filter(p => p.status !== 'pending').length / syncProgress.length) * 100}
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary" align="center">
                {syncProgress.filter(p => p.status !== 'pending').length} of {syncProgress.length} sites processed
              </Typography>
            </Box>
            
            <List>
              {syncProgress.map((progress) => (
                <ListItem key={progress.siteId}>
                  <ListItemIcon>
                    {progress.status === 'pending' && <CircularProgress size={20} />}
                    {progress.status === 'syncing' && <CircularProgress size={20} />}
                    {progress.status === 'success' && <CheckCircle color="success" />}
                    {progress.status === 'error' && <Error color="error" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={progress.siteName}
                    secondary={progress.message}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            {!syncing && syncProgress.length > 0 && (
              <Button 
                onClick={startSync}
                variant="contained"
                startIcon={<Sync />}
              >
                Start Sync
              </Button>
            )}
            <Button 
              onClick={closeSyncModal}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : 'Close'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ListSiteForms;